import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { authGet, authPost } from '../../services/apiService';

const DARK_GREEN = '#1D4434';

const TIER_COLS = [
  {
    key: 'tier1',
    label: 'Tier 1 — Action Blocked',
    color: '#D74B00',
    hint: 'Value ≥ threshold → client is action-blocked.',
  },
  {
    key: 'tier2',
    label: 'Tier 2 — Adjustable Limit',
    color: '#F59E0B',
    hint: 'Value ≥ threshold → limit is adjustable by admin.',
  },
  {
    key: 'tier3',
    label: 'Tier 3 — UI Warning',
    color: '#10B981',
    hint: 'Value ≥ threshold → a UI warning is shown.',
  },
];

const GuardrailTierClassificationTab = ({ onConfigSaved }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await authGet('/api/guardrail/config', null, 'json', { useCache: false });
        setRules(data.tierRules || []);
      } catch (err) {
        setError('Failed to load tier configuration.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = (idx, tier, value) => {
    setRules(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [tier]: value };
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await authPost('/api/guardrail/config', { tierRules: rules });
      setSnackOpen(true);
      onConfigSaved?.();
    } catch (err) {
      setError('Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (rules.length === 0) {
    return (
      <Alert severity="info">
        No data uploaded yet. Go to the <strong>Data Upload</strong> tab to import the Excel file first.
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2.5,
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ color: DARK_GREEN, fontWeight: 600 }}>
            Tier Classification Rules
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Set numeric thresholds per metric. Clients with a value ≥ threshold are assigned that
            tier. Tier 1 takes priority over Tier 2 and Tier 3.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ bgcolor: DARK_GREEN, '&:hover': { bgcolor: '#2D6A4F' }, whiteSpace: 'nowrap' }}
        >
          {saving ? 'Saving…' : 'Save Configuration'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={2}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  bgcolor: '#F3F4F6',
                  color: '#374151',
                  width: '30%',
                  borderBottom: '2px solid rgba(0,0,0,0.12)',
                }}
              >
                Metric Field
              </TableCell>
              {TIER_COLS.map(({ key, label, color, hint }) => (
                <TableCell
                  key={key}
                  align="center"
                  sx={{
                    fontWeight: 700,
                    bgcolor: color,
                    color: 'white',
                    width: '23%',
                    borderBottom: '2px solid rgba(0,0,0,0.12)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    {label}
                    <Tooltip title={hint} arrow placement="top">
                      <InfoOutlinedIcon sx={{ fontSize: 14, opacity: 0.85, cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((rule, idx) => (
              <TableRow
                key={rule.field}
                hover
                sx={{ '&:nth-of-type(even)': { bgcolor: 'rgba(0,0,0,0.02)' } }}
              >
                <TableCell sx={{ fontWeight: 500, color: '#1F2937' }}>{rule.label || rule.field}</TableCell>
                {TIER_COLS.map(({ key, color }) => (
                  <TableCell key={key} align="center">
                    <TextField
                      size="small"
                      type="number"
                      placeholder="—"
                      value={rule[key] != null ? rule[key] : ''}
                      onChange={e => handleChange(idx, key, e.target.value === '' ? null : e.target.value)}
                      inputProps={{ style: { textAlign: 'center' }, min: 0, step: 'any' }}
                      sx={{
                        width: 120,
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: color },
                          '&.Mui-focused fieldset': { borderColor: color },
                        },
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnackOpen(false)} sx={{ width: '100%' }}>
          Tier configuration saved. Dashboard will reflect the new rules.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GuardrailTierClassificationTab;
